import { resolveReceiptPaperWidth, type ReceiptData } from '@mlm/shared';
import type { Branch, PosCheckoutResult } from './api';

/**
 * The tape for a sale that JUST completed, straight from the checkout response.
 *
 * Deliberately not `orderToReceipt`: the response carries the activation PINs
 * and the tendered / change figures as they were at the moment of sale. The
 * receipt page falls back to a fetched order (and that shared mapper) only when
 * this response is gone, e.g. after a reload.
 *
 * Buyer and cashier names come off the response too, not the sale state, which
 * has already been reset for the next customer.
 */
export function checkoutToReceipt(
  receipt: PosCheckoutResult,
  /** The selling branch: company header, field flags and paper width. */
  branch: Branch | null | undefined,
): ReceiptData {
  return {
    orderCode: receipt.order.code,
    status: receipt.order.status,
    items: receipt.items,
    subtotal: receipt.order.subtotal,
    discountTotal: receipt.order.discountTotal,
    couponTotal: receipt.order.couponTotal,
    couponLabel: receipt.order.couponLabel,
    couponCode: receipt.order.couponCode,
    cashierDiscountAmount: receipt.order.managerDiscountAmount,
    total: receipt.order.total,
    payment: {
      method: receipt.payment.method,
      methodLabel: receipt.payment.methodLabel,
      amountTendered: receipt.payment.amountTendered,
      changeDue: receipt.payment.changeDue,
    },
    kitCodes: receipt.kitCodes,
    header: branch?.receiptHeader ?? null,
    customFeeLabel: receipt.order.customFeeLabel,
    customFeeAmount: receipt.order.customFeeAmount,
    customFees: receipt.order.customFees,
    memberName: receipt.buyerName,
    cashierName: receipt.cashierName,
    branchName: branch?.name ?? null,
    createdAt: receipt.order.createdAt,
    remarks: receipt.order.notes,
    fields: branch?.receiptFields,
    paperWidth: resolveReceiptPaperWidth(branch?.receiptPaperWidth),
  };
}
